<template>
  <div class="page-table-layout information-requests-landing" data-testid="information-requests">
    <v-card
      class="all-width flex-card shadow-off"
      loader-height="2"
      :loading="isLoading"
      :disabled="isLoading"
      data-testid="information-requests__main-card"
    >
      <v-card-text class="page-table-layout__body gap-2">
        <template v-if="!isLoading">
          <InformationRequestsPageHeader
            :activity-resource-id="cycleId"
            :import-loading="uploadLoading"
            :can-edit="canEdit"
            :has-requests="requestList.length > 0"
            :can-add-request="canAddRequest"
            :show-request-actions="activeTab === 'requests'"
            @refresh="refreshData"
            @download-all="onDownloadAll"
            @new-request="onNewRequest"
            @import="openBulkUploadModal"
          />

          <FcTabSwitcher
            v-model="activeTab"
            variant="underline"
            aria-label="Information Requests tabs"
            :tabs="tabs"
            data-testid="information-requests__tab-switcher"
            @input="onTabChange"
          />

          <template v-if="activeTab === 'requests'">
            <RequestTableToolbar
              :loading="isLoading"
              :metadata="projectMetadata"
              :selected-tab-stage="selectedTabStage"
              :active-summary="activeSummary"
              :count="filteredRequestList.length"
              :has-active-table-filter="hasActiveTableFilter"
              :headers="tableHeaders"
              @select-stage="onSelectStage"
              @clear-filter="onClearTableFilter"
              @apply-columns="onApplyColumns"
            />

            <div class="flex-fill-table">
              <RequestTable
                ref="requestTable"
                :loading="isLoading"
                :paginated-items="paginatedItems"
                :request-list="filteredRequestList"
                :original-request-list="requestList"
                :original-request-count="requestList.length"
                :filter-options-list="filterOptionsList"
                :headers="tableHeaders"
                :items-per-page="itemsPerPage"
                :current-page="currentPage"
                :selected-request-ids.sync="selectedRequestIds"
                :select-filter.sync="selectFilter"
                :page-details="pageDetails"
                :location-list="locationList"
                :search-query="searchQuery"
                :table-height="responsiveTableHeight"
                :can-edit="canEdit"
                :can-delete="canDelete"
                @filter-item="onFilterItem"
                @clear-item="onClearFilterItem"
                @filter-active-change="hasActiveTableFilter = $event"
                @update:headers="onApplyColumns"
                @navigate-detail="onNavigateDetail"
                @row-action="onRowAction"
              />
            </div>

            <bulk-selection-bar
              v-if="selectedRequestIds.length > 0"
              :selected-count="selectedRequestIds.length"
              section-label="EDIT FIELDS"
              :actions="bulkSelectionActions"
              :loading="notifyLoading || bulkEditOwnerLoading"
              test-id-prefix="information-requests"
              dismiss-test-id="information-requests__bulk-dismiss-btn"
              bottom-offset="var(--space-md, 16px)"
              @action="onBulkSelectionAction"
              @clear-selection="clearSelection"
            />
          </template>

          <template v-else>
            <InformationOwnerTableToolbar
              :search-query.sync="ownerSearchQuery"
              :loading="isLoading"
              :can-edit="canEdit"
              :has-active-table-filter="ownerHasActiveTableFilter"
              @clear-filter="onOwnerClearTableFilter"
              @add-owner="openAddOwnerModal"
            />

            <div class="flex-fill-table information-requests-landing__owners-tab">
              <InformationOwnerTable
                ref="ownerTable"
                :loading="isLoading"
                :owner-rows="ownerTableRows"
                :displayed-rows="displayedOwnerRows"
                :search-query="ownerSearchQuery"
                :select-filter="ownerSelectFilter"
                :can-edit="canEdit"
                :can-delete="canDelete"
                :action-loading-id="ownerActionLoadingId"
                :action-type="ownerActionType"
                @filter-item="onOwnerFilterItem"
                @clear-item="onOwnerClearFilterItem"
                @filter-active-change="ownerHasActiveTableFilter = $event"
                @add-owner="openAddOwnerModal"
                @follow-up="onOwnerFollowUp"
                @toggle-status="onOwnerToggleStatus"
                @edit="onOwnerEdit"
                @reassign="onOwnerReassign"
                @delete="onOwnerDelete"
              />
            </div>
          </template>
        </template>

        <v-skeleton-loader
          v-else
          type="table-heading, list-item-two-line, article"
          data-testid="information-requests__loading-skeleton"
        />
      </v-card-text>
    </v-card>

    <Pagination
      v-if="activeTab === 'requests' && !isLoading"
      ref="paginationComponent"
      :key="paginationKey"
      :list="filteredRequestList"
      :itemsperpage="itemsPerPage"
      data-testid="information-requests__pagination"
      @paginated-data="setPaginatedData"
    />

    <NotifyModal
      v-model="notifyModalOpen"
      :loading="notifyLoading"
      :suggested-contacts="emailSuggestions"
      :info-owner-list="infoOwnerList"
      :email-template="emailSummaryTemplate"
      :selected-items="notifySelectedIds"
      :project-name="projectSummary.name || projectMetadata.description || ''"
      :user-name="$store.state.user.name || ''"
      :user-email="$store.state.user.email || ''"
      @send="onSendNotify"
    />

    <AddEditOwnerModal
      v-model="ownerModalOpen"
      :loading="ownerModalLoading"
      :owner="editingOwner"
      :server-errors="ownerFormErrors"
      @save="onSaveOwner"
      @clear-server-error="clearOwnerFormError"
    />

    <AddEditRequestModal
      v-model="requestModalOpen"
      :loading="requestModalLoading"
      :request="editingRequest"
      :errors="requestFormErrors"
      :info-owner-list="infoOwnerList"
      :priority-list="priorityList"
      :category-list="categoryList"
      :location-list="locationList"
      :location-header="$store.state.LocationHeader || 'Location'"
      :min-date="$nova.formatDateISO(new Date())"
      :task-options="formattedRefList"
      :project-id="cycleId"
      @save="onSaveRequest"
      @clear-error="clearRequestFormError"
    />

    <BulkUpload
      v-model="bulkUploadModalOpen"
      external
      external-mode="information-request"
      test-id-prefix="information-request"
      :busy="uploadLoading"
      :validating="bulkUploadParsing"
      :request-upload-data="bulkUploadData"
      :error-flag="bulkUploadErrorFlag"
      :error-list="bulkUploadErrorList"
      :request-headers="bulkUploadHeaders"
      @download-template="onDownloadTemplate"
      @file-selected="onBulkUploadFileSelected"
      @reupload="onBulkUploadReupload"
      @upload="onBulkUploadSubmit"
    />

    <BulkEditOwnerModal
      v-model="bulkEditOwnerModalOpen"
      :loading="bulkEditOwnerLoading"
      :selected-count="bulkEditOwnerSelectedCount"
      :info-owner-list="bulkEditOwnerListForModal"
      :error="bulkEditOwnerError"
      @replace="onBulkEditOwnerReplace"
    />

    <DeleteCancelConfirmModal
      v-model="confirmDialog.open"
      :variant="confirmDialog.variant"
      :loading="confirmDialog.loading"
      :custom-title="confirmDialog.title"
      :custom-message="confirmDialog.message"
      @confirm="onConfirmAction"
    />
  </div>
</template>

<script>
import './InformationRequests.scss';
import { mapGetters } from 'vuex';
import { FcTabSwitcher, Pagination, BulkUpload } from '../../../components';
import {
  InformationRequestsPageHeader,
  RequestTable,
  RequestTableToolbar,
  InformationOwnerTable,
  InformationOwnerTableToolbar,
} from './components';
import {
  NotifyModal,
  AddEditOwnerModal,
  AddEditRequestModal,
  BulkEditOwnerModal,
  DeleteCancelConfirmModal,
} from './modals';
import { REQUEST_ACTION_IDS } from './utils/requestActions';
import {
  buildRequestActiveSummary,
  filterRequestsByStage,
  prepareRequestList,
} from './utils/requestList';
import {
  buildOwnerTableRows,
  filterOwnersBySearch,
  sortOwnersForTable,
} from './utils/ownerSummary';
import { parseOwnerSaveErrorResponse } from './utils/infoOwner';
import {
  INFORMATION_MANAGER_TABLE_HEADERS,
  INFORMATION_MANAGER_STAGE_LABEL_MAP,
  INFORMATION_MANAGER_PRIORITY_LIST,
  IM_CSV_TABLE_HEADERS,
  TABLE_OFFSET_SMALL_SCREEN,
  TABLE_OFFSET_LARGE_SCREEN,
  LARGE_SCREEN_HEIGHT_THRESHOLD,
} from '../../../utils/constants';
import {
  resolveStageLabel,
  buildRequestUpdatePayload,
  validateInformationRequest,
} from '../../../utils/informationManager';
import {
  buildRequestExportRows,
  buildCsvDataUri,
  prepareRequestsForUpdate,
} from '../../../utils/informationManagerComponentUtils';
import { readCSVClient } from '../../../utils/utils';

const LOG_PREFIX = '[InformationRequests]:';

/** True when navigating back from an Information Request detail route. */
function isFromInformationRequestDetail(from) {
  const path = from?.path || '';
  return /\/informationrequests\/[^/]+\/request\//.test(path)
    || /\/informationmanager\/[^/]+\/request\//.test(path);
}

const TABS = [
  { id: 'requests', label: 'Requests' },
  { id: 'owners', label: 'Information Owners' },
];

export default {
  name: 'financialclose_project_informationrequests',
  components: {
    FcTabSwitcher,
    Pagination,
    InformationRequestsPageHeader,
    RequestTable,
    RequestTableToolbar,
    NotifyModal,
    AddEditOwnerModal,
    AddEditRequestModal,
    BulkUpload,
    BulkEditOwnerModal,
    DeleteCancelConfirmModal,
    InformationOwnerTable,
    InformationOwnerTableToolbar,
  },
  data() {
    const stageLabelMap = { ...INFORMATION_MANAGER_STAGE_LABEL_MAP };
    return {
      right: 'financialclose_pbc_request',
      cycleId: this.$route.params.id,
      activeTab: this.$route.query.tab === 'owners' ? 'owners' : 'requests',
      selectedTabStage: null,
      stageLabelMap,
      tableHeaders: INFORMATION_MANAGER_TABLE_HEADERS(stageLabelMap).filter((h) => h.value !== 'action'),
      filteredRequestList: [],
      activeColumnFilter: null,
      selectFilter: {},
      pageDetails: {},
      searchQuery: '',
      itemsPerPage: 25,
      currentPage: 1,
      paginatedItems: [],
      selectedRequestIds: [],
      hasActiveTableFilter: false,
      paginationKey: 0,
      notifyModalOpen: false,
      notifyLoading: false,
      notifySelectedIds: [],
      emailSuggestions: [],
      confirmDialog: {
        open: false,
        variant: 'delete',
        title: '',
        message: '',
        loading: false,
        action: null,
        payload: null,
      },
      requestModalOpen: false,
      requestModalLoading: false,
      editingRequest: null,
      requestFormErrors: {},
      isFreePlan: false,
      bulkUploadModalOpen: false,
      bulkUploadParsing: false,
      bulkUploadData: [],
      bulkUploadErrorFlag: false,
      bulkUploadErrorList: [],
      bulkUploadHeaders: IM_CSV_TABLE_HEADERS,
      bulkEditOwnerModalOpen: false,
      bulkEditOwnerLoading: false,
      bulkEditOwnerError: '',
      ownerReassignRequestIds: [],
      ownerReassignSourceId: null,
      priorityList: [...INFORMATION_MANAGER_PRIORITY_LIST],
      pendingListViewFilter: null,
      windowHeight: window.innerHeight,
      ownerSearchQuery: '',
      ownerSelectFilter: {},
      ownerActiveColumnFilter: null,
      ownerHasActiveTableFilter: false,
      ownerModalOpen: false,
      ownerModalLoading: false,
      editingOwner: null,
      ownerFormErrors: {},
      ownerActionLoadingId: null,
      ownerActionType: null,
    };
  },
  computed: {
    ...mapGetters('informationManager', {
      isLoading: 'isLoading',
      uploadLoading: 'isUploadLoading',
      projectMetadata: 'projectMetadata',
      requestList: 'requestList',
      infoOwnerList: 'infoOwnerList',
      allInfoOwnerList: 'allInfoOwnerList',
      storeFilteredLocationList: 'filteredLocationList',
      storeLocationList: 'locationList',
      projectSummary: 'projectSummary',
      emailSummaryTemplate: 'emailSummaryTemplate',
      storeCompleteChecklist: 'completeChecklist',
    }),
    pbcProjectId() {
      return this.projectMetadata?.pbcproject || '';
    },
    locationList() {
      return this.storeFilteredLocationList?.length
        ? this.storeFilteredLocationList
        : (this.storeLocationList || []);
    },
    tabs() {
      return TABS;
    },
    activeSummary() {
      return buildRequestActiveSummary(this.projectSummary);
    },
    canEdit() {
      return this.$nova.hasRight(this.right, 'edit') || this.$nova.hasRight(this.right, 'add');
    },
    canDelete() {
      return this.$nova.hasRight(this.right, 'delete');
    },
    canAddRequest() {
      return this.projectMetadata?.stage === 0 && this.canEdit;
    },
    isManager() {
      const userId = this.$store.state.user?.id || this.$store.state.user?._id || '';
      const pbcManagerList = this.projectSummary?.managerlist || [];
      const projectManagerList = this.projectMetadata?.managerlist || [];
      return pbcManagerList.some((id) => String(id) === String(userId))
        || projectManagerList.some((id) => String(id) === String(userId));
    },
    filterOptionsList() {
      let list = prepareRequestList(this.requestList);
      list = filterRequestsByStage(list, this.selectedTabStage);
      return list;
    },
    responsiveTableHeight() {
      const offset = this.windowHeight >= LARGE_SCREEN_HEIGHT_THRESHOLD
        ? TABLE_OFFSET_LARGE_SCREEN
        : TABLE_OFFSET_SMALL_SCREEN;
      return `calc(100vh - ${offset}px)`;
    },
    ownerTableRows() {
      return buildOwnerTableRows(this.allInfoOwnerList, this.requestList);
    },
    filteredOwnerRows() {
      let rows = filterOwnersBySearch(this.ownerTableRows, this.ownerSearchQuery);
      if (this.ownerActiveColumnFilter?.item) {
        rows = this.$nova.filterByDynamicCriteria(rows, this.ownerActiveColumnFilter.item);
      }
      return sortOwnersForTable(rows);
    },
    displayedOwnerRows() {
      return this.filteredOwnerRows;
    },
    categoryList() {
      const categories = ['Others'];
      (this.requestList || []).forEach((request) => {
        if (request.category && request.category !== '' && !categories.includes(request.category)) {
          categories.push(request.category);
        }
      });
      return categories;
    },
    formattedRefList() {
      const map = new Map();
      (this.requestList || []).forEach((req) => {
        (req.referenceChecklist || []).forEach((cl) => {
          if (cl._id && !map.has(cl._id)) {
            map.set(cl._id, {
              value: cl._id,
              name: cl.description,
              master_id: cl.master_id,
              ref: cl.ref,
              location: cl.location,
            });
          }
        });
      });
      return Array.from(map.values());
    },
    bulkEditOwnerSelectedCount() {
      if (this.ownerReassignRequestIds.length) {
        return this.ownerReassignRequestIds.length;
      }
      return this.selectedRequestIds.length;
    },
    bulkEditOwnerListForModal() {
      const owners = this.infoOwnerList || [];
      if (!this.ownerReassignSourceId) return owners;
      const sourceId = String(this.ownerReassignSourceId);
      return owners.filter((owner) => String(owner._id) !== sourceId);
    },
    bulkSelectionActions() {
      const actions = [];

      if (this.isManager) {
        actions.push({
          id: 'notify',
          label: 'Send Status Email',
          iconSrc: '/img/icons/fbc-icons/BulkSendEmail.svg',
          testId: 'information-requests__bulk-notify-btn',
        });
      }

      actions.push({
        id: 'edit-owner',
        label: 'Re-Assign',
        iconSrc: '/img/icons/fbc-icons/BulkReassign.svg',
        testId: 'information-requests__bulk-edit-owner-btn',
      });

      if (this.canEdit) {
        actions.push({
          id: 'cancel-requests',
          label: 'Cancel Request',
          icon: 'mdi-close-circle-outline',
          testId: 'information-requests__bulk-cancel-btn',
        });
      }

      actions.push({
        id: 'export',
        label: 'Export',
        iconSrc: '/img/icons/fbc-icons/BulkExport.svg',
        testId: 'information-requests__bulk-export-btn',
      });

      return actions;
    },
  },
  watch: {
    requestList: {
      handler() {
        this.rebuildFilteredList();
      },
      deep: true,
    },
    '$route.query.tab'(tab) {
      const nextTab = tab === 'owners' ? 'owners' : 'requests';
      if (nextTab !== this.activeTab) {
        this.activeTab = nextTab;
      }
    },
    bulkEditOwnerModalOpen(open) {
      if (!open) {
        this.ownerReassignRequestIds = [];
        this.ownerReassignSourceId = null;
      }
    },
  },
  beforeRouteEnter(to, from, next) {
    // Task list / task detail → list: refresh. Detail → list: reuse Vuex cache.
    to.meta.useCachedInformationRequests = isFromInformationRequestDetail(from);
    next();
  },
  async created() {
    window.addEventListener('resize', this.handleResize);
    this.isFreePlan = this.$store.state.subscriptions?.pbc?.plan_detail?.code === 'pbc_free';
    this.seedPendingFilterFromListViewState();
    await this.bootstrap();
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
    this.ownerSearchQuery = '';
    this.saveListViewState({ ownerSearchQuery: '' });
  },
  methods: {
    handleResize() {
      this.windowHeight = window.innerHeight;
    },
    async bootstrap() {
      const projectId = this.cycleId;
      if (!projectId) return;

      const storeProjectId = this.$store.getters['informationManager/projectId'];
      const hasCachedData = String(storeProjectId) === String(projectId)
        && Object.keys(this.projectMetadata || {}).length > 0;
      // Only skip network when returning from request detail with Vuex already warm.
      const useCache = this.$route.meta.useCachedInformationRequests && hasCachedData;

      if (!useCache) {
        await this.refreshData({ rebuild: false });
      }

      this.applyListViewState();
      this.rebuildFilteredList({ resetPage: !useCache });
      this.syncTableFilterFromPending();

      if (useCache && this.currentPage > 1) {
        const savedPage = this.currentPage;
        this.$nextTick(() => {
          this.$refs.paginationComponent?.setPage?.(savedPage);
        });
      }
    },
    /**
     * Reload project context + requests. Concurrent callers share one in-flight promise
     * so bootstrap, header refresh, and post-mutation refreshes do not duplicate APIs.
     */
    async refreshData({ rebuild = true } = {}) {
      if (this._refreshPromise) return this._refreshPromise;

      this._refreshPromise = (async () => {
        try {
          const contextResult = await this.$store.dispatch('informationManager/fetchProjectContext', {
            projectId: this.cycleId,
          });
          if (contextResult !== 'success') {
            console.warn(`${LOG_PREFIX} fetchProjectContext failed`);
            return;
          }
          await this.$store.dispatch('informationManager/fetchRequests');
          if (rebuild) {
            this.rebuildFilteredList();
          }
        } finally {
          this._refreshPromise = null;
        }
      })();

      return this._refreshPromise;
    },
    seedPendingFilterFromListViewState() {
      const saved = this.$store.getters['informationManager/getListViewState'](this.cycleId);
      if (saved?.tableFilterHeader && Object.keys(saved.tableFilterHeader).length > 0) {
        this.pendingListViewFilter = { ...saved.tableFilterHeader };
      }
      if (saved?.activeTab) {
        this.activeTab = saved.activeTab;
      }
      if (saved?.selectedTabStage !== undefined) {
        this.selectedTabStage = saved.selectedTabStage;
      }
      if (typeof saved?.itemsPerPage === 'number' && saved.itemsPerPage > 0) {
        this.itemsPerPage = saved.itemsPerPage;
      }
      if (typeof saved?.currentPage === 'number' && saved.currentPage > 0) {
        this.currentPage = saved.currentPage;
      }
      if (saved?.searchQuery) {
        this.searchQuery = saved.searchQuery;
      }
      if (saved?.ownerTableFilterHeader && Object.keys(saved.ownerTableFilterHeader).length > 0) {
        this.ownerSelectFilter = { ...saved.ownerTableFilterHeader };
        this.ownerActiveColumnFilter = { item: { ...saved.ownerTableFilterHeader } };
      }
    },
    applyListViewState() {
      const saved = this.$store.getters['informationManager/getListViewState'](this.cycleId);
      if (!saved) return;

      if (saved.activeTab && this.$route.query.tab !== saved.activeTab) {
        this.activeTab = saved.activeTab;
        this.syncTabQueryParam(saved.activeTab);
      }
      if (saved.selectedTabStage !== undefined) {
        this.selectedTabStage = saved.selectedTabStage;
      }
      if (typeof saved.itemsPerPage === 'number' && saved.itemsPerPage > 0) {
        this.itemsPerPage = saved.itemsPerPage;
      }
      if (saved.tableFilterHeader && Object.keys(saved.tableFilterHeader).length > 0) {
        this.selectFilter = { ...saved.tableFilterHeader };
        this.activeColumnFilter = { item: { ...saved.tableFilterHeader } };
      }
    },
    /**
     * Sync ?tab= in the address bar without vue-router navigation.
     * base.vue keys router-view on $route.fullPath — router.replace remounts the page
     * and re-runs bootstrap (getdetails / getdata / infoowner list).
     * Same pattern as TasksLandingPage.syncViewModeQueryParam.
     */
    syncTabQueryParam(tabId) {
      const query = { ...this.$route.query, tab: tabId };
      const { href } = this.$router.resolve({ path: this.$route.path, query });
      window.history.replaceState(window.history.state, '', href);
    },
    buildListQuery(extra = {}) {
      const query = { ...extra };
      const cid = this.$route.query.cid || this.$store.state.client;
      if (cid) {
        query.cid = cid;
      }
      return query;
    },
    syncTableFilterFromPending() {
      if (!this.pendingListViewFilter) return;
      this.selectFilter = { ...this.pendingListViewFilter };
      this.activeColumnFilter = { item: { ...this.pendingListViewFilter } };
      this.pendingListViewFilter = null;
    },
    saveListViewState(partial = {}) {
      const projectId = this.cycleId;
      if (!projectId) return;

      const tableSnapshot = this.$refs.requestTable?.getListViewSnapshot?.() || {};
      const payload = {
        activeTab: this.activeTab,
        selectedTabStage: this.selectedTabStage,
        searchQuery: this.searchQuery,
        currentPage: this.currentPage,
        itemsPerPage: this.itemsPerPage,
        tableFilterHeader: tableSnapshot.tableFilterHeader || this.selectFilter,
        ...partial,
      };

      this.$store.dispatch('informationManager/saveListViewState', {
        projectId,
        partial: payload,
      });
    },
    rebuildFilteredList({ resetPage = true } = {}) {
      let list = prepareRequestList(this.requestList);
      list = filterRequestsByStage(list, this.selectedTabStage);
      if (this.activeColumnFilter?.item) {
        list = this.$nova.filterByDynamicCriteria(list, this.activeColumnFilter.item);
      }
      this.filteredRequestList = list;
      this.syncPaginatedItems({ resetPage });
      if (resetPage) {
        this.resetPaginationToFirstPage();
      }
    },
    syncPaginatedItems({ resetPage = true } = {}) {
      const list = this.filteredRequestList || [];
      const totalPages = Math.max(1, Math.ceil(list.length / this.itemsPerPage) || 1);
      let page = resetPage ? 1 : (this.currentPage || 1);
      if (page > totalPages) {
        page = 1;
      }
      if (page < 1) {
        page = 1;
      }
      const start = (page - 1) * this.itemsPerPage;
      const end = page * this.itemsPerPage;
      this.paginatedItems = list.slice(start, end);
      this.currentPage = page;
    },
    resetPaginationToFirstPage() {
      this.paginationKey += 1;
      this.$nextTick(() => {
        this.$refs.paginationComponent?.setPage?.(1);
      });
    },
    setPaginatedData(paginationInfo) {
      if (paginationInfo && typeof paginationInfo === 'object' && 'data' in paginationInfo) {
        this.paginatedItems = paginationInfo.data || [];
        this.currentPage = paginationInfo.page || 1;
      } else {
        this.paginatedItems = paginationInfo || [];
      }
      this.saveListViewState({ currentPage: this.currentPage });
    },
    onTabChange(tabId) {
      this.activeTab = tabId;
      this.saveListViewState({ activeTab: tabId });
      this.syncTabQueryParam(tabId);
    },
    clearSelection() {
      this.selectedRequestIds = [];
    },
    onSelectStage(stage) {
      this.clearSelection();
      this.selectedTabStage = stage;
      this.saveListViewState({ selectedTabStage: stage });
      this.rebuildFilteredList();
    },
    onFilterItem(item) {
      this.clearSelection();
      this.activeColumnFilter = { item: JSON.parse(JSON.stringify(item)) };
      this.selectFilter = { ...item };
      this.saveListViewState({ tableFilterHeader: this.selectFilter });
      this.rebuildFilteredList();
    },
    onClearFilterItem() {
      this.clearSelection();
      this.activeColumnFilter = null;
      this.selectFilter = {};
      this.saveListViewState({ tableFilterHeader: {} });
      this.rebuildFilteredList();
    },
    onClearTableFilter() {
      this.$refs.requestTable?.clearTableFilter();
      this.onClearFilterItem();
    },
    onApplyColumns(headers) {
      this.tableHeaders = headers;
    },
    onNavigateDetail(request) {
      this.saveListViewState();
      this.$router.push({
        path: `/financialclose/project/informationrequests/${this.cycleId}/request/${request._id}`,
        query: this.buildListQuery({ tab: this.activeTab }),
      });
    },
    onRowAction({ actionId, request }) {
      switch (actionId) {
        case REQUEST_ACTION_IDS.EDIT_REQUEST:
          this.openEditRequestModal(request);
          break;
        case REQUEST_ACTION_IDS.NOTIFY:
          this.openNotifyModal([request._id]);
          break;
        case REQUEST_ACTION_IDS.APPROVE:
          this.changeRequestStage(request._id, 2);
          break;
        case REQUEST_ACTION_IDS.REJECT:
          this.changeRequestStage(request._id, 0);
          break;
        case REQUEST_ACTION_IDS.CANCEL:
          this.openConfirm('cancel', request._id);
          break;
        case REQUEST_ACTION_IDS.DELETE:
          this.openConfirm('delete', request._id);
          break;
        case REQUEST_ACTION_IDS.RESTORE:
          this.changeRequestStage(request._id, 0);
          break;
        case REQUEST_ACTION_IDS.DOWNLOAD:
        case REQUEST_ACTION_IDS.DOWNLOAD_ALL:
          this.$refs.requestTable?.downloadAllAttachments(request);
          break;
        default:
          break;
      }
    },
    openConfirm(variant, payload, customMessage = '') {
      this.confirmDialog = {
        open: true,
        variant,
        title: '',
        message: customMessage,
        loading: false,
        action: variant,
        payload,
      };
    },
    async onConfirmAction() {
      const { action, payload } = this.confirmDialog;
      this.confirmDialog.loading = true;
      try {
        if (action === 'cancel') {
          await this.changeRequestStage(payload, -1);
        } else if (action === 'bulk-cancel') {
          const lineIds = Array.isArray(payload) ? payload : [];
          const result = await this.$store.dispatch('informationManager/bulkCancelRequests', {
            projectId: this.pbcProjectId,
            lineIds,
          });
          if (result === 'success') {
            this.selectedRequestIds = [];
            this.rebuildFilteredList();
          }
        } else if (action === 'delete') {
          await this.$store.dispatch('informationManager/deleteRequest', {
            requestId: payload,
            cycleId: this.cycleId,
          });
        } else if (action === 'delete-owner') {
          this.ownerActionLoadingId = payload;
          this.ownerActionType = 'delete';
          try {
            const response = await this.$store.dispatch('informationManager/deleteInfoOwner', { id: payload, pbcProjectId: this.pbcProjectId });
            if (response?.status === 'success') {
              await this.refreshData();
            }
          } finally {
            this.ownerActionLoadingId = null;
            this.ownerActionType = null;
          }
        }
      } finally {
        this.confirmDialog.loading = false;
        this.confirmDialog.open = false;
      }
    },
    async changeRequestStage(requestId, stage) {
      await this.$store.dispatch('informationManager/changeRequestStage', { requestId, stage });
      this.rebuildFilteredList();
    },
    async openNotifyModal(selectedIds) {
      this.notifySelectedIds = [...selectedIds];
      this.notifyLoading = true;
      try {
        await Promise.all([
          this.$store.dispatch('informationManager/fetchEmailTemplate'),
          this.loadEmailSuggestions(),
        ]);
        this.notifyModalOpen = true;
      } finally {
        this.notifyLoading = false;
      }
    },
    async loadEmailSuggestions() {
      this.emailSuggestions = await this.$store.dispatch('informationManager/fetchEmailSuggestions', {
        projectId: this.pbcProjectId,
      });
    },
    async onSendNotify(emailData) {
      this.notifyLoading = true;
      try {
        const result = await this.$store.dispatch('informationManager/triggerEmailSummary', {
          projectId: this.pbcProjectId,
          selectedItems: emailData.selectedItems,
          to: emailData.to,
          cc: emailData.cc,
          body: emailData.body,
          subject: emailData.subject,
        });
        if (result === 'success') {
          this.notifyModalOpen = false;
          this.selectedRequestIds = [];
        }
      } finally {
        this.notifyLoading = false;
      }
    },
    onBulkSelectionAction(actionId) {
      if (actionId === 'notify') {
        this.openNotifyModal(this.selectedRequestIds);
        return;
      }
      if (actionId === 'edit-owner') {
        this.onBulkEditOwner();
        return;
      }
      if (actionId === 'cancel-requests') {
        this.onBulkCancelRequests();
        return;
      }
      if (actionId === 'export') {
        this.onExportSelected();
      }
    },
    onBulkEditOwner() {
      this.ownerReassignRequestIds = [];
      this.ownerReassignSourceId = null;
      this.bulkEditOwnerError = '';
      this.bulkEditOwnerModalOpen = true;
    },
    onOwnerReassign(owner) {
      if (!owner?._id) return;
      const requestIds = this.getRequestIdsForOwner(owner._id);
      if (!requestIds.length) {
        this.$store.commit('sbError', 'No requests found for this information owner', { root: true });
        return;
      }
      this.ownerReassignRequestIds = requestIds;
      this.ownerReassignSourceId = owner._id;
      this.bulkEditOwnerError = '';
      this.bulkEditOwnerModalOpen = true;
    },
    getRequestIdsForOwner(ownerId) {
      const id = String(ownerId);
      return (this.requestList || [])
        .filter((request) => String(request?.info_owner) === id)
        .map((request) => request._id)
        .filter(Boolean);
    },
    async onBulkEditOwnerReplace(ownerId) {
      const lineIds = this.ownerReassignRequestIds.length
        ? this.ownerReassignRequestIds
        : this.selectedRequestIds;
      if (!ownerId || lineIds.length === 0) return;
      this.bulkEditOwnerLoading = true;
      try {
        const result = await this.$store.dispatch('informationManager/bulkUpdateInfoOwner', {
          projectId: this.pbcProjectId,
          data: [{ lineids: lineIds, info_owner: ownerId }],
        });
        if (result === 'success') {
          this.bulkEditOwnerModalOpen = false;
          this.selectedRequestIds = [];
          this.ownerReassignRequestIds = [];
          this.ownerReassignSourceId = null;
          this.bulkEditOwnerError = '';
        } else {
          this.bulkEditOwnerError = 'Failed to replace information owner';
        }
      } finally {
        this.bulkEditOwnerLoading = false;
      }
    },
    onBulkCancelRequests() {
      if (this.selectedRequestIds.length === 0) return;
      const count = this.selectedRequestIds.length;
      this.confirmDialog = {
        open: true,
        variant: 'cancel',
        title: count === 1 ? 'Cancel Request' : 'Cancel Requests',
        message: `Are you sure you want to cancel ${count} request${count === 1 ? '' : 's'}? This action cannot be undone.`,
        loading: false,
        action: 'bulk-cancel',
        payload: [...this.selectedRequestIds],
      };
    },
    onDownloadTemplate() {
      this.$store.dispatch('informationManager/downloadInfoManagerTemplate');
    },
    onDownloadAll() {
      this.exportRequests(this.requestList);
    },
    onExportSelected() {
      const selectedIdSet = new Set(this.selectedRequestIds.map((id) => String(id)));
      const selectedRequests = (this.requestList || []).filter((request) => (
        selectedIdSet.has(String(request._id))
      ));
      this.exportRequests(selectedRequests);
    },
    exportRequests(requests = []) {
      const projectDueDateLabel = this.projectMetadata?.duedate
        ? this.$nova.formatDate(new Date(this.projectMetadata.duedate))
        : '';
      const csvRows = buildRequestExportRows({
        projectName: this.projectSummary?.name || this.projectMetadata?.description || '',
        projectDueDate: projectDueDateLabel,
        requests,
        getLocationName: (locationId) => this.getLocationName(locationId),
        getStageLabel: (stage) => resolveStageLabel(stage, this.stageLabelMap),
        formatDate: (value) => this.$nova.formatDate(value),
      });
      const csvContent = buildCsvDataUri(csvRows);
      const link = document.createElement('a');
      link.href = csvContent;
      link.download = 'request_list.csv';
      link.click();
    },
    getLocationName(locationId) {
      const locations = this.locationList?.length
        ? this.locationList
        : (this.$store.getters['informationManager/locationList'] || []);
      const match = locations.find((loc) => String(loc._id) === String(locationId));
      return match?.name || locationId || '';
    },
    onNewRequest() {
      this.editingRequest = null;
      this.requestFormErrors = {};
      this.requestModalOpen = true;
    },
    openEditRequestModal(request) {
      this.editingRequest = { ...request };
      this.requestFormErrors = {};
      this.requestModalOpen = true;
    },
    clearRequestFormError(field) {
      if (this.requestFormErrors[field]) {
        this.$set(this.requestFormErrors, field, '');
      }
    },
    async onSaveRequest({ mode, id, data }) {
      const requestData = { ...data };
      if (mode === 'edit' && id) {
        requestData._id = id;
      }

      const { errors, hasError } = validateInformationRequest(requestData);
      this.requestFormErrors = errors;
      if (hasError) {
        this.$store.commit('sbError', 'Please fill all required fields correctly.', { root: true });
        return;
      }

      this.requestModalLoading = true;
      try {
        if (mode === 'edit') {
          const payload = buildRequestUpdatePayload(requestData);
          const response = await this.$store.dispatch('informationManager/editRequest', {
            requestId: payload._id,
            data: payload,
          });
          if (response?.status === 'success') {
            this.requestModalOpen = false;
            this.editingRequest = null;
            this.rebuildFilteredList();
          } else if (response?.data?.[0]?.index0) {
            this.requestFormErrors = response.data[0].index0;
          }
          return;
        }

        const [normalizedRequest] = prepareRequestsForUpdate(
          [requestData],
          {
            projectId: this.pbcProjectId,
            cycleId: this.cycleId,
            isFreePlan: this.isFreePlan,
          },
        );
        const requestBatch = normalizedRequest ? [normalizedRequest] : [];
        const response = await this.$store.dispatch('informationManager/addRequest', { data: requestBatch });
        if (response?.status === 'success') {
          this.requestModalOpen = false;
          this.editingRequest = null;
          const createdId = response.data?.[0]?._id;
          if (createdId) {
            this.onNavigateDetail({ _id: createdId });
          } else {
            this.rebuildFilteredList();
          }
        } else if (response?.data?.[0]?.index0) {
          this.requestFormErrors = response.data[0].index0;
        }
      } finally {
        this.requestModalLoading = false;
      }
    },
    openBulkUploadModal() {
      this.bulkUploadData = [];
      this.bulkUploadErrorFlag = false;
      this.bulkUploadErrorList = [];
      this.bulkUploadModalOpen = true;
    },
    onBulkUploadReupload() {
      this.bulkUploadData = [];
      this.bulkUploadErrorFlag = false;
      this.bulkUploadErrorList = [];
      this.bulkUploadParsing = false;
    },
    buildRefListFromChecklist(checklist = []) {
      const map = new Map();
      (checklist || []).forEach((cl) => {
        if (cl._id && !map.has(cl._id) && cl.ref) {
          map.set(cl._id, {
            value: cl._id,
            name: cl.description,
            master_id: cl.master_id,
            ref: cl.ref,
            location: cl.location,
            locationName: cl.__computed?.locationName || cl.locationName || '',
          });
        }
      });
      return Array.from(map.values());
    },
    async onBulkUploadFileSelected(file) {
      if (!file) return;
      this.bulkUploadParsing = true;
      this.bulkUploadData = [];
      this.bulkUploadErrorFlag = false;
      this.bulkUploadErrorList = [];
      try {
        const { pbcuploaddata, pbcerrorlist, errorFlag } = await readCSVClient(
          file,
          [],
          this.$nova.readExcelFile.bind(this.$nova),
          this.allInfoOwnerList,
        );

        let nextData = pbcuploaddata;
        let nextErrorList = pbcerrorlist;
        let nextErrorFlag = errorFlag;

        if (pbcuploaddata.length && this.cycleId) {
          const items = pbcuploaddata.map((row) => {
            const entry = row.data[0] || {};
            return {
              ref: entry.ref || [],
              category: entry.category || '',
              description: entry.description || '',
              info_owner_name: entry.info_owner_name || '',
              info_owner_email: entry.info_owner_email || '',
              type: entry.type,
              uploadlink: entry.uploadlink || '',
              priority: entry.priority || '',
              duedate: entry.duedate || '',
              location: entry.location || '',
            };
          });
          const validation = await this.$store.dispatch('informationManager/validateBulkPbc', {
            projectId: this.cycleId,
            items,
          });
          if (validation && validation.valid === false) {
            // Apply onto a working copy before revealing preview.
            this.bulkUploadData = nextData;
            this.bulkUploadErrorList = nextErrorList;
            this.bulkUploadErrorFlag = nextErrorFlag;
            this.applyBulkPbcRowErrors(validation.rowErrors || []);
            nextData = this.bulkUploadData;
            nextErrorList = this.bulkUploadErrorList;
            nextErrorFlag = this.bulkUploadErrorFlag;
          }
        }

        this.bulkUploadData = nextData;
        this.bulkUploadErrorList = nextErrorList;
        this.bulkUploadErrorFlag = nextErrorFlag;
      } catch (err) {
        this.$store.commit('sbError', err.message || err || 'Not able to read CSV file', { root: true });
        console.error(`${LOG_PREFIX} bulk upload parse error`, err);
      } finally {
        this.bulkUploadParsing = false;
      }
    },

    applyBulkPbcRowErrors(rowErrors = []) {
      if (!Array.isArray(rowErrors) || !rowErrors.length) return;
      const errorList = [...this.bulkUploadErrorList];
      rowErrors.forEach((entry) => {
        const idx = Number(entry.row) - 1;
        if (!Number.isFinite(idx) || idx < 0 || !this.bulkUploadData[idx]) return;
        const fields = entry.fields || {};
        const messages = Object.values(fields).filter(Boolean);
        if (!messages.length) return;
        const rowEntry = this.bulkUploadData[idx].data[0];
        const existing = rowEntry.errors ? `${rowEntry.errors}; ` : '';
        rowEntry.errors = `${existing}${messages.join('; ')}`;
        messages.forEach((msg) => {
          if (!errorList.includes(msg)) errorList.push(msg);
        });
      });
      this.bulkUploadErrorList = errorList;
      this.bulkUploadErrorFlag = true;
    },
    async onBulkUploadSubmit() {
      if (this.bulkUploadData.length === 0 || this.bulkUploadErrorFlag) return;
      const items = this.bulkUploadData.map((row) => {
        const entry = row.data[0] || {};
        let type = entry.type || '';
        if (type === 'fileuploadtolink') type = 'link';
        return {
          ref: entry.ref || [],
          category: entry.category || '',
          description: entry.description || '',
          info_owner_name: entry.info_owner_name || '',
          info_owner_email: entry.info_owner_email || '',
          type,
          uploadlink: type === 'link' ? (entry.uploadlink || '') : '',
          priority: entry.priority || 'Medium',
          duedate: entry.duedate || '',
          location: entry.location || '',
        };
      });
      const status = await this.$store.dispatch('informationManager/bulkUploadPbc', {
        projectId: this.cycleId,
        items,
      });
      if (status === 'success') {
        this.bulkUploadModalOpen = false;
        this.bulkUploadData = [];
        await this.refreshData();
      }
    },
    openAddOwnerModal() {
      this.editingOwner = null;
      this.ownerFormErrors = {};
      this.ownerModalOpen = true;
    },
    onOwnerEdit(owner) {
      if (!owner?._id) return;
      this.editingOwner = { ...owner };
      this.ownerFormErrors = {};
      this.ownerModalOpen = true;
    },
    clearOwnerFormError(field) {
      if (this.ownerFormErrors[field]) {
        this.$set(this.ownerFormErrors, field, '');
      }
    },
    applyOwnerSaveErrors(response) {
      const { fieldErrors, message } = parseOwnerSaveErrorResponse(response);
      this.ownerFormErrors = fieldErrors;
      this.$store.commit('sbError', message, { root: true });
    },
    async onSaveOwner({ mode, id, data, sendWelcomeEmail }) {
      this.ownerModalLoading = true;
      try {
        if (mode === 'edit') {
          const response = await this.$store.dispatch('informationManager/editInfoOwner', { id, data, pbcProjectId: this.pbcProjectId });
          if (response?.status === 'success') {
            this.ownerModalOpen = false;
            this.editingOwner = null;
            this.ownerFormErrors = {};
          } else {
            this.applyOwnerSaveErrors(response);
          }
          return;
        }

        const response = await this.$store.dispatch('informationManager/addInfoOwner', { data, pbcProjectId: this.pbcProjectId });
        if (response?.status === 'success' && response.data?.[0]?._id) {
          const newOwnerId = response.data[0]._id;
          this.ownerModalOpen = false;
          this.editingOwner = null;
          this.ownerFormErrors = {};

          if (sendWelcomeEmail) {
            await this.$store.dispatch('informationManager/sendMail', {
              projectId: this.pbcProjectId,
              data: [{ info_owner: [newOwnerId], type: 'reminder' }],
            });
          }
          await this.refreshData();
        } else {
          this.applyOwnerSaveErrors(response);
        }
      } finally {
        this.ownerModalLoading = false;
      }
    },
    async onOwnerFollowUp(owner) {
      if (!owner?._id) return;
      this.ownerActionLoadingId = owner._id;
      this.ownerActionType = 'follow-up';
      try {
        await this.$store.dispatch('informationManager/sendMail', {
          projectId: this.pbcProjectId,
          data: [{ info_owner: [owner._id], type: 'reminder' }],
        });
      } finally {
        this.ownerActionLoadingId = null;
        this.ownerActionType = null;
      }
    },
    async onOwnerToggleStatus({ owner, enabled }) {
      if (!owner?._id) return;
      this.ownerActionLoadingId = owner._id;
      this.ownerActionType = 'toggle';
      try {
        await this.$store.dispatch('informationManager/editInfoOwner', {
          id: owner._id,
          data: {
            name: owner.name,
            email: owner.email,
            department: owner.department || '',
            status: Boolean(enabled),
          },
          pbcProjectId: this.pbcProjectId,
        });
      } finally {
        this.ownerActionLoadingId = null;
        this.ownerActionType = null;
      }
    },
    onOwnerDelete(owner) {
      this.openConfirm(
        'delete-owner',
        owner._id,
        `Are you sure you want to delete ${owner.name || owner.email}? This cannot be undone.`,
      );
    },
    onOwnerFilterItem(item) {
      this.ownerActiveColumnFilter = { item: JSON.parse(JSON.stringify(item)) };
      this.ownerSelectFilter = { ...item };
      this.saveListViewState({ ownerTableFilterHeader: this.ownerSelectFilter });
    },
    onOwnerClearFilterItem() {
      this.ownerActiveColumnFilter = null;
      this.ownerSelectFilter = {};
      this.saveListViewState({ ownerTableFilterHeader: {} });
    },
    onOwnerClearTableFilter() {
      this.$refs.ownerTable?.clearTableFilter();
      this.onOwnerClearFilterItem();
    },
  },
};
</script>

